import { useEffect } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  VStack,
} from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ActionIconButton, DeleteOutline } from '@aslanpay/portal-common-lib';

import useFetchIntegrationConfigSpec from 'app/hooks/useFetchIntegrationConfigSpec';

import ConfigSpecFieldInput from './ConfigSpecFieldInput';
import { ConnectionFormValues } from './types';
import { applicableConfigFields } from './utils';

interface ConnectionSettingsFieldsProps {
  /** Provider of the connection; drives which spec'd config fields are rendered. */
  provider?: string;
  /** Protocol of the connection; protocol-scoped fields only render when it matches. */
  protocol?: string;
  /** Integration types selected (register) or enabled (edit); type-scoped fields only render when theirs is included. */
  integrationTypes?: string[];
}

/** Base URL + provider-settings editor, shared by the register and edit modals. Settings the provider
 * declares in its config field spec render as dedicated inputs (dropdowns for SELECT fields); anything
 * else stays in the free key/value list below. */
function ConnectionSettingsFields({
  provider = undefined,
  protocol = undefined,
  integrationTypes = [],
}: ConnectionSettingsFieldsProps) {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<ConnectionFormValues>();

  const { data: specFields = [] } = useFetchIntegrationConfigSpec(provider);
  const visibleFields = applicableConfigFields(specFields, { protocol, integrationTypes });

  const {
    fields: configFields,
    append: appendConfigEntry,
    remove: removeConfigEntry,
    replace: replaceConfigEntries,
  } = useFieldArray({ control, name: 'configEntries' });

  // On edit, the stored config is prefilled into the free key/value list before the spec is known.
  // Once the spec loads, absorb the spec'd keys into their dedicated fields and keep the rest listed.
  useEffect(() => {
    if (specFields.length === 0) {
      return;
    }
    const specKeys = specFields.map((field) => field.key);
    const entries = getValues('configEntries') ?? [];
    const absorbed = entries.filter((entry) => specKeys.includes(entry.key.trim()));
    if (absorbed.length === 0) {
      return;
    }
    absorbed.forEach((entry) => setValue(`configValues.${entry.key.trim()}`, entry.value));
    replaceConfigEntries(entries.filter((entry) => !specKeys.includes(entry.key.trim())));
  }, [specFields, getValues, setValue, replaceConfigEntries]);

  return (
    <>
      <FormControl isInvalid={!!errors.baseUrl} isRequired>
        <FormLabel>Base URL</FormLabel>
        <Input type="url" placeholder="https://<host>" {...register('baseUrl')} />
        <FormHelperText>
          Host root only — provider settings like the Workday tenant are appended automatically.
        </FormHelperText>
        <FormErrorMessage>{errors.baseUrl?.message}</FormErrorMessage>
      </FormControl>

      {visibleFields.map((field) => (
        <ConfigSpecFieldInput key={field.key} field={field} />
      ))}

      <FormControl>
        <FormLabel>Additional settings</FormLabel>
        <VStack align="stretch" spacing="8px">
          {configFields.map((configField, index) => (
            <HStack key={configField.id} align="flex-start">
              <FormControl isInvalid={!!errors.configEntries?.[index]?.key}>
                <Input placeholder="Key" {...register(`configEntries.${index}.key`)} />
              </FormControl>
              <FormControl isInvalid={!!errors.configEntries?.[index]?.value}>
                <Input placeholder="Value" {...register(`configEntries.${index}.value`)} />
              </FormControl>
              <ActionIconButton
                size="md"
                colorScheme="red"
                icon={<DeleteOutline boxSize="16px" />}
                aria-label={`Remove setting ${index + 1}`}
                onClick={() => removeConfigEntry(index)}
              />
            </HStack>
          ))}
          <Button
            size="xs"
            variant="ghost"
            alignSelf="flex-start"
            onClick={() => appendConfigEntry({ key: '', value: '' })}
          >
            Add setting
          </Button>
        </VStack>
      </FormControl>
    </>
  );
}

export default ConnectionSettingsFields;
